import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";

const LineChart = ({ title, categories, series, revenue, filter }) => {
  const [chartOptions, setChartOptions] = useState({
    chart: {
      type: "line",
      height: 350,
      zoom: { enabled: false },
      toolbar: { show: false },
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    xaxis: {
      categories: [],
      title: { text: "Time Period" },
    },
    yaxis: {
      title: { text: "Value" },
      labels: {
        formatter: (value) => {
          // Format based on data type
          if (title && title.toLowerCase().includes('revenue')) {
            return `$${value.toLocaleString()}`;
          }
          return value.toString();
        },
      },
    },
    tooltip: {
      x: { formatter: (value, { dataPointIndex, w }) => {
        return w.globals.categoryLabels[dataPointIndex];
      }},
      y: { 
        formatter: (value) => {
          if (title && title.toLowerCase().includes('revenue')) {
            return `$${value.toLocaleString()}`;
          }
          return value.toString();
        }
      },
    },
    legend: { horizontalAlign: "left" },
    colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0'],
  });

  const [chartSeries, setChartSeries] = useState([]);

  // Transform backend data for revenue charts
  const transformRevenueData = () => {
    if (!revenue?.data) return { categories: [], series: [] };
    
    const { data } = revenue;
    
    if (filter?.filter === "yearly") {
      const yearlyData = Object.entries(data)
        .filter(([key]) => /^\d{4}$/.test(key))
        .sort(([a], [b]) => parseInt(a) - parseInt(b))
        .map(([year, value]) => ({ category: year, value }));
      
      return {
        categories: yearlyData.map(item => item.category),
        series: [{
          name: "Revenue",
          data: yearlyData.map(item => item.value)
        }]
      };
    } else if (filter?.filter === "monthly") {
      const monthlyData = Object.entries(data)
        .map(([month, value]) => ({ category: month, value }));
      
      return {
        categories: monthlyData.map(item => item.category),
        series: [{
          name: "Revenue",
          data: monthlyData.map(item => item.value)
        }]
      };
    } else if (filter?.filter === "weekly") {
      const weeklyData = Object.entries(data)
        .filter(([key]) =>
          /^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|Week\s+\d+)$/i.test(key)
        )
        .map(([day, value]) => ({ category: day, value }));
      
      return {
        categories: weeklyData.map(item => item.category),
        series: [{
          name: "Revenue",
          data: weeklyData.map(item => item.value)
        }]
      };
    } else if (filter?.filter === "daily") {
      const dailyData = Object.entries(data)
        .map(([day, value]) => ({ category: day, value }));
      
      return {
        categories: dailyData.map(item => item.category),
        series: [{
          name: "Revenue",
          data: dailyData.map(item => item.value)
        }]
      };
    }
    
    return { categories: [], series: [] };
  };

  useEffect(() => {
    let finalCategories = [];
    let finalSeries = [];

    // Use props data if available (from Dashboard mock data)
    if (categories && series) {
      finalCategories = categories;
      finalSeries = series;
    } 
    // Otherwise use revenue data with filter transformation
    else if (revenue && filter) {
      const transformed = transformRevenueData();
      finalCategories = transformed.categories;
      finalSeries = transformed.series;
    }

    // Update chart options
    setChartOptions(prevOptions => ({
      ...prevOptions,
      xaxis: {
        ...prevOptions.xaxis,
        categories: finalCategories,
        title: {
          text: filter?.filter ? 
            `${filter.filter.charAt(0).toUpperCase() + filter.filter.slice(1)} Period` : 
            "Time Period"
        },
      },
      title: {
        text: title || "Chart",
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'bold'
        }
      }
    }));

    setChartSeries(finalSeries);
  }, [categories, series, revenue, filter, title]);

  // Show loading or no data state
  if (!chartSeries.length || chartSeries[0]?.data?.length === 0) {
    return (
      <div style={{ 
        height: '350px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        border: '1px solid #e0e0e0',
        borderRadius: '4px'
      }}>
        <div style={{ textAlign: 'center', color: '#666' }}>
          <h6>{title || "Chart"}</h6>
          <p>No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ReactApexChart
        options={chartOptions}
        series={chartSeries}
        type="line"
        height={350}
      />
    </div>
  );
};

export default LineChart;